import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Box,
	Button,
	Container,
	Typography,
	Card,
	CircularProgress,
	Alert,
	AppBar,
	Toolbar,
	IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DiamondIcon from "@mui/icons-material/Diamond";
import StripeService from "../../services/stripe/StripeService";

const PageContainer = styled(Box)(() => ({
	minHeight: "100vh",
	background: "linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)",
}));

const StyledAppBar = styled(AppBar)(() => ({
	background: "#ffffff",
	boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
}));

const ContentContainer = styled(Container)(() => ({
	paddingTop: "40px",
	paddingBottom: "40px",
	display: "flex",
	justifyContent: "center",
}));

const PaymentCard = styled(Card)(() => ({
	maxWidth: 500,
	width: "100%",
	padding: "48px",
	borderRadius: "16px",
	boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
	textAlign: "center",
}));

const PlanIcon = styled(Box, {
	shouldForwardProp: (prop) => prop !== "planType",
})<{ planType: "plus" | "pro" }>(({ planType }) => ({
	width: "80px",
	height: "80px",
	borderRadius: "20px",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	margin: "0 auto 24px auto",
	background:
		planType === "plus"
			? "linear-gradient(135deg, #5DBEC3 0%, #4BA3A7 100%)"
			: "linear-gradient(135deg, #ffc107 0%, #ff9800 100%)",
	color: "#ffffff",
}));

const PriceDisplay = styled(Box)(() => ({
	marginBottom: "32px",
}));

const FeaturesList = styled(Box)(() => ({
	textAlign: "left",
	marginBottom: "32px",
}));

const PayButton = styled(Button, {
	shouldForwardProp: (prop) => prop !== "planType",
})<{ planType: "plus" | "pro" }>(({ planType }) => ({
	background:
		planType === "plus"
			? "linear-gradient(135deg, #5DBEC3 0%, #4BA3A7 100%)"
			: "linear-gradient(135deg, #ffc107 0%, #ff9800 100%)",
	color: "#ffffff",
	fontWeight: 600,
	fontSize: "18px",
	textTransform: "none",
	borderRadius: "12px",
	padding: "16px 48px",
	width: "100%",
	transition: "all 0.3s ease",
	"&:hover": {
		background:
			planType === "plus"
				? "linear-gradient(135deg, #4BA3A7 0%, #3d8a8e 100%)"
				: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
		transform: "translateY(-2px)",
		boxShadow: "0 8px 24px rgba(93, 190, 195, 0.3)",
	},
	"&:disabled": {
		background: "#dee2e6",
		color: "#6c757d",
		transform: "none",
	},
}));

const PaymentPage: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const stripeService = StripeService.getInstance();

	const plan = searchParams.get("plan") || "plus";
	const price = searchParams.get("price") || "20";

	const planDetails = {
		plus: {
			name: "Plus Plan",
			icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
			features: [
				"Everything in Free",
				"Advanced analytics & insights",
				"Unlimited facilities",
				"Custom reporting",
				"API access",
				"Priority email support",
				"Goal setting & tracking",
				"Benchmarking tools",
			],
		},
		pro: {
			name: "Pro Plan",
			icon: <DiamondIcon sx={{ fontSize: 40 }} />,
			features: [
				"Everything in Plus",
				"White-label options",
				"Dedicated account manager",
				"Custom integrations",
				"Advanced compliance reporting",
				"24/7 phone support",
				"Custom training sessions",
				"SLA guarantees",
			],
		},
	};

	const currentPlan =
		planDetails[plan as keyof typeof planDetails] || planDetails.plus;

	const handlePayment = async () => {
		if (!isAuthenticated) {
			navigate("/");
			return;
		}

		setLoading(true);
		setError("");

		try {
			const token = await getAccessTokenSilently();

			// Map plan names to Stripe price IDs
			// These should be configured in your environment or fetched from backend
			const priceIds = {
				plus:
					process.env.REACT_APP_STRIPE_PLUS_PRICE_ID ||
					"price_plus_monthly",
				pro:
					process.env.REACT_APP_STRIPE_PRO_PRICE_ID ||
					"price_pro_monthly",
			};

			// Create checkout session through backend API
			const response = await fetch(
				`${process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN}/api/subscription/create-checkout-session`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						priceId: priceIds[plan as keyof typeof priceIds],
						successUrl: `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}`,
						cancelUrl: `${window.location.origin}/modern-pricing`,
					}),
				},
			);

			if (!response.ok) {
				throw new Error("Failed to create checkout session");
			}

			const { sessionId } = await response.json();

			// Redirect to Stripe Checkout
			// redirectToCheckout will throw an error internally if it fails
			await stripeService.redirectToCheckout(sessionId);
		} catch (err: any) {
			console.error("Payment error:", err);
			setError(
				err.message ||
					"Failed to initialize payment. Please try again.",
			);
		} finally {
			setLoading(false);
		}
	};

	const handleBack = () => {
		navigate("/modern-pricing");
	};

	if (isLoading) {
		return (
			<PageContainer>
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					minHeight="100vh"
				>
					<CircularProgress sx={{ color: "#5DBEC3" }} />
				</Box>
			</PageContainer>
		);
	}

	return (
		<PageContainer>
			<StyledAppBar position="sticky">
				<Toolbar>
					<IconButton onClick={handleBack} sx={{ color: "#5DBEC3" }}>
						<ArrowBackIcon />
					</IconButton>
					<Typography
						variant="h6"
						sx={{
							marginLeft: 2,
							color: "#2c3e50",
							fontWeight: 600,
						}}
					>
						Complete Your Subscription
					</Typography>
				</Toolbar>
			</StyledAppBar>

			<ContentContainer>
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					style={{ width: "100%" }}
				>
					<PaymentCard>
						<PlanIcon planType={plan as "plus" | "pro"}>
							{currentPlan.icon}
						</PlanIcon>

						<Typography
							variant="h4"
							sx={{
								fontWeight: 700,
								color: "#2c3e50",
								marginBottom: "8px",
							}}
						>
							{currentPlan.name}
						</Typography>

						<PriceDisplay>
							<Typography
								variant="h2"
								sx={{
									fontWeight: 700,
									color:
										plan === "plus" ? "#5DBEC3" : "#ff9800",
									marginBottom: "4px",
								}}
							>
								${price}
							</Typography>
							<Typography
								variant="body1"
								sx={{ color: "#6c757d" }}
							>
								per month
							</Typography>
						</PriceDisplay>

						<FeaturesList>
							<Typography
								variant="h6"
								sx={{
									fontWeight: 600,
									color: "#2c3e50",
									marginBottom: "16px",
								}}
							>
								What's included:
							</Typography>
							{currentPlan.features.map((feature, index) => (
								<Box
									key={index}
									sx={{
										display: "flex",
										alignItems: "center",
										marginBottom: "12px",
									}}
								>
									<CheckIcon
										sx={{
											color:
												plan === "plus"
													? "#5DBEC3"
													: "#ff9800",
											marginRight: "12px",
											fontSize: 20,
										}}
									/>
									<Typography
										variant="body1"
										sx={{ color: "#2c3e50" }}
									>
										{feature}
									</Typography>
								</Box>
							))}
						</FeaturesList>

						{error && (
							<Alert
								severity="error"
								sx={{ marginBottom: "24px" }}
							>
								{error}
							</Alert>
						)}

						<PayButton
							planType={plan as "plus" | "pro"}
							onClick={handlePayment}
							disabled={loading}
						>
							{loading ? (
								<CircularProgress
									size={24}
									sx={{ color: "#fff" }}
								/>
							) : (
								`Subscribe for $${price}/month`
							)}
						</PayButton>

						<Typography
							variant="body2"
							sx={{
								color: "#6c757d",
								marginTop: "16px",
								fontSize: "14px",
							}}
						>
							Secure payment processed by Stripe. Cancel anytime.
						</Typography>
					</PaymentCard>
				</motion.div>
			</ContentContainer>
		</PageContainer>
	);
};

export default PaymentPage;
